import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Printer, Truck } from 'lucide-react';
import {
  addDays,
  formatDeliveryWindow,
  formatMoney,
  shopDate,
  type DeliveryRouteDay,
} from '@bakery/shared';

import { api } from '../lib/api.js';
import { printDeliveryRoute } from '../lib/printing.js';
import { OrderDetail } from './Orders.js';
import { CustomerDetail } from './Customers.js';
import { Badge, Button, cx, EmptyState, Input, ROOMY_TARGET, Skeleton, useToast } from '../components/ui.js';

/**
 * The round, for whoever is driving it.
 *
 * The shop wrote this out by hand every afternoon — the deliveries grouped by
 * community, with the address and a phone number copied off each order — so
 * this looks like that sheet and prints like it. It opens already filled in,
 * ordered village by village, and the whole point is that it can be rearranged
 * before it is printed: the person driving knows the estate better than any
 * sort order does.
 *
 * Every cell takes typing, because the addresses behind it are one line of free
 * text somebody typed into a chat and the guess at where the community ends is
 * sometimes wrong. A correction sticks; emptying a cell hands it back to the
 * order, so being wrong here costs nothing.
 */
export function DeliveryRouteScreen() {
  const [today, setToday] = useState(() => shopDate());
  const [date, setDate] = useState(today);

  const day = useQuery({
    queryKey: ['delivery-route', date],
    queryFn: () => api.deliveryRoute.day(date),
    // An order taken at another till has to reach the sheet before the rider
    // leaves, and half a minute is invisible on shop wifi.
    refetchInterval: 30_000,
  });

  /*
   * The sheet opens on today and stays on today — the same rule as the bake
   * sheet, for the same reason: a shop screen is left up for days, and a view
   * still showing yesterday at five in the afternoon is a round nobody drives.
   */
  useEffect(() => {
    const tick = () => {
      const now = shopDate();
      if (now === today) return;
      setDate((current) => (current === today ? now : current));
      setToday(now);
    };
    const timer = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [today]);

  const weekday = useMemo(
    () =>
      new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      }),
    [date],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-lg font-semibold">Delivery route</h1>
          <p className="text-xs text-[var(--color-ink-soft)]">
            {weekday}
            {day.data && (
              <>
                {' · '}
                {day.data.stops.length} {day.data.stops.length === 1 ? 'stop' : 'stops'} · {day.data.itemCount}{' '}
                {day.data.itemCount === 1 ? 'item' : 'items'}
                {day.data.balanceDue > 0 && <> · {formatMoney(day.data.balanceDue)} to collect</>}
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            aria-label="Previous day"
            onClick={() => setDate((current) => addDays(current, -1))}
            icon={<ChevronLeft className="size-4" />}
          />
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="w-40" />
          <Button
            size="sm"
            variant="ghost"
            aria-label="Next day"
            onClick={() => setDate((current) => addDays(current, 1))}
            icon={<ChevronRight className="size-4" />}
          />
          <Button size="sm" variant={date === today ? 'primary' : undefined} onClick={() => setDate(today)}>
            Today
          </Button>
        </div>
      </header>

      {day.isLoading && <Skeleton className="m-4 h-64" />}
      {day.data && <Route day={day.data} />}
    </div>
  );
}

/** The delivery window, as the customer was told it. */
function windowFor(dueAt: string | null): string {
  return dueAt ? formatDeliveryWindow(dueAt) : '';
}

function Route({ day }: { day: DeliveryRouteDay }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * Opened over the sheet rather than instead of it, as on the bake sheet.
   * Checking what somebody ordered should not cost the place in the round.
   */
  const [openOrder, setOpenOrder] = useState<string | null>(null);
  const [openCustomer, setOpenCustomer] = useState<string | null>(null);

  const statuses = useQuery({ queryKey: ['order-statuses'], queryFn: () => api.orders.statuses() });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['delivery-route', day.date] });

  const save = useMutation({
    mutationFn: ({ orderId, values }: { orderId: string; values: Record<string, string> }) =>
      api.deliveryRoute.set(day.date, orderId, values),
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const reorder = useMutation({
    mutationFn: (orderIds: string[]) => api.deliveryRoute.reorder(day.date, orderIds),
    onSuccess: refresh,
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not reorder', body: error.message }),
  });

  if (day.stops.length === 0) {
    return (
      <EmptyState
        icon={<Truck className="size-10" />}
        title="Nothing out for delivery"
        description="Orders set for delivery on this date will appear here, ready to print for the rider."
      />
    );
  }

  /**
   * Move one stop up or down, and send the whole sequence.
   *
   * The whole list goes rather than the two that moved, because a position is
   * only meaningful relative to the rest — and a reorder that saved half would
   * leave the round in an order nobody chose.
   */
  const nudge = (index: number, by: -1 | 1) => {
    const swap = index + by;
    if (swap < 0 || swap >= day.stops.length) return;

    const ids = day.stops.map((stop) => stop.orderId);
    const moved = ids[index]!;
    ids[index] = ids[swap]!;
    ids[swap] = moved;
    reorder.mutate(ids);
  };

  const missing = day.stops.filter((stop) => stop.needsAddress).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2">
        <Button size="sm" icon={<Printer className="size-4" />} onClick={() => printDeliveryRoute(day)}>
          Print for the rider
        </Button>

        {/*
          * Said once, at the top, rather than only as a mark on the row that
          * has it. A rider who notices a blank address at the gate is already
          * out; whoever prints the sheet should know before they go.
          */}
        {missing > 0 && (
          <span className="text-xs text-[var(--color-warn)]">
            {missing === 1 ? 'One stop has no address' : `${missing} stops have no address`} on file — type it in
            below.
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-ink-soft)]">
              <th className="border-b border-[var(--color-line)] px-2 py-2 font-medium">#</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 font-medium">Community</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 font-medium">Name</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 font-medium">Address</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 font-medium">Contact #</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 font-medium">Notes</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 text-right font-medium">Items</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2 text-right font-medium">To collect</th>
              <th className="border-b border-[var(--color-line)] px-2 py-2" />
            </tr>
          </thead>

          <tbody>
            {day.stops.map((stop, index) => (
              <tr
                key={stop.orderId}
                className={cx(
                  'align-top',
                  // A stop nobody can drive to, called out on its own row too.
                  stop.needsAddress && 'bg-[var(--color-warn-soft)]',
                )}
              >
                <td className="tabular border-b border-[var(--color-line)] px-2 py-1.5 text-[var(--color-ink-soft)]">
                  {stop.position}
                </td>

                <td className="border-b border-[var(--color-line)] px-1 py-1">
                  {/*
                    * Empty rather than a dash. These are editable boxes, and a
                    * placeholder that looks like a value is a value somebody
                    * types around instead of into.
                    */}
                  <TextCell
                    value={stop.community}
                    placeholder=""
                    overridden={stop.overridden.community}
                    onSave={(community) => save.mutate({ orderId: stop.orderId, values: { community } })}
                  />
                </td>

                <td className="border-b border-[var(--color-line)] px-2 py-1.5">
                  <div className="flex flex-col gap-0.5">
                    {stop.customerId ? (
                      <button
                        type="button"
                        onClick={() => setOpenCustomer(stop.customerId)}
                        className={cx(
                          'text-left font-medium underline decoration-dotted underline-offset-2 hover:text-[var(--color-accent)]',
                          ROOMY_TARGET,
                        )}
                      >
                        {stop.customerName}
                      </button>
                    ) : (
                      <span className="font-medium">{stop.customerName}</span>
                    )}

                    <button
                      type="button"
                      onClick={() => setOpenOrder(stop.orderId)}
                      className={cx(
                        'text-left text-xs text-[var(--color-ink-soft)] underline decoration-dotted underline-offset-2 hover:text-[var(--color-accent)]',
                        ROOMY_TARGET,
                      )}
                    >
                      {stop.orderNo}
                    </button>

                    <span className="text-xs text-[var(--color-ink-faint)]">{windowFor(stop.dueAt)}</span>
                  </div>
                </td>

                <td className="border-b border-[var(--color-line)] px-1 py-1">
                  <TextCell
                    value={stop.addressLine}
                    placeholder="No address on file"
                    wide
                    overridden={stop.overridden.addressLine}
                    onSave={(addressLine) => save.mutate({ orderId: stop.orderId, values: { addressLine } })}
                  />
                </td>

                <td className="border-b border-[var(--color-line)] px-1 py-1">
                  <TextCell
                    value={stop.contact}
                    placeholder=""
                    overridden={stop.overridden.contact}
                    onSave={(contact) => save.mutate({ orderId: stop.orderId, values: { contact } })}
                  />
                </td>

                <td className="border-b border-[var(--color-line)] px-1 py-1">
                  <TextCell
                    value={stop.notes}
                    placeholder=""
                    wide
                    boxed
                    overridden={stop.overridden.notes}
                    onSave={(notes) => save.mutate({ orderId: stop.orderId, values: { notes } })}
                  />
                </td>

                <td className="tabular border-b border-[var(--color-line)] px-2 py-1.5 text-right">{stop.itemCount}</td>

                <td className="tabular border-b border-[var(--color-line)] px-2 py-1.5 text-right">
                  {stop.balanceDue > 0 ? (
                    <Badge tone="warn">{formatMoney(stop.balanceDue)}</Badge>
                  ) : (
                    <span className="text-xs text-[var(--color-ink-faint)]">Paid</span>
                  )}
                </td>

                <td className="border-b border-[var(--color-line)] px-1 py-1">
                  <div className="flex gap-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Move ${stop.orderNo} earlier`}
                      disabled={index === 0 || reorder.isPending}
                      onClick={() => nudge(index, -1)}
                      icon={<ChevronUp className="size-4" />}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Move ${stop.orderNo} later`}
                      disabled={index === day.stops.length - 1 || reorder.isPending}
                      onClick={() => nudge(index, 1)}
                      icon={<ChevronDown className="size-4" />}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr className="font-medium">
              <td className="px-2 py-2" />
              <td className="px-2 py-2 text-xs uppercase tracking-wide text-[var(--color-ink-soft)]" colSpan={5}>
                {day.stops.length} {day.stops.length === 1 ? 'stop' : 'stops'}
              </td>
              <td className="tabular px-2 py-2 text-right">{day.itemCount}</td>
              <td className="tabular px-2 py-2 text-right">
                {day.balanceDue > 0 ? formatMoney(day.balanceDue) : '—'}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {openOrder && (
        <OrderDetail orderId={openOrder} statuses={statuses.data ?? []} onClose={() => setOpenOrder(null)} />
      )}
      {openCustomer && <CustomerDetail customerId={openCustomer} onClose={() => setOpenCustomer(null)} />}
    </div>
  );
}

/**
 * One editable cell of the sheet.
 *
 * Saves as it is left rather than on a button, so a row can be tabbed across
 * while reading an address off a message. A cell somebody has typed over is
 * marked, because the rest are guesses worked out from one line of free text
 * and the difference is worth seeing at a glance.
 */
function TextCell({
  value,
  placeholder,
  overridden,
  wide,
  boxed,
  onSave,
}: {
  value: string;
  placeholder: string;
  overridden: boolean;
  wide?: boolean;
  /**
   * Draw the box even when the cell is empty and nobody is in it.
   *
   * The other columns arrive with something in them, so the text is its own
   * invitation to click. Notes almost always starts empty, and an empty
   * borderless input is indistinguishable from a blank cell — there was nothing
   * on screen to say the column could be written in at all.
   */
  boxed?: boolean;
  onSave: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  return (
    <input
      value={draft}
      placeholder={placeholder}
      onFocus={() => setEditing(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        if (draft !== value) onSave(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          setDraft(value);
          event.currentTarget.blur();
        }
      }}
      className={cx(
        'w-full rounded-[var(--radius-sm)] px-1.5 py-1 text-sm',
        'placeholder:text-[var(--color-ink-faint)]',
        'focus:bg-[var(--color-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]',
        boxed
          ? 'border border-[var(--color-line)] bg-[var(--color-surface-sunken)]'
          : 'border border-transparent bg-transparent',
        wide ? 'min-w-48' : 'min-w-28',
        // A dotted underline for a cell that was corrected by hand.
        overridden && 'underline decoration-dotted decoration-[var(--color-accent)] underline-offset-4',
      )}
    />
  );
}
